// Newsletter signup with RGPD checkbox.
// Used in Footer and on the dedicated /newsletter standalone page.
// Tracks the user's current language so the campaign sender can target
// the right locale automatically.
import { useState } from "react";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { useT, useLang } from "@/lib/i18n";
import { apiFetch } from "@/lib/api";

export const NewsletterSignup = ({ source = "footer", variant = "footer" }) => {
  const t = useT();
  const lang = useLang();
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState(""); // honeypot — must stay empty
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | sending | ok | err
  const [errorMsg, setErrorMsg] = useState("");

  const consentText = t("newsletter.consent.label");

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) {
      setErrorMsg(t("newsletter.error.consent_required"));
      setStatus("err");
      return;
    }
    setStatus("sending");
    setErrorMsg("");
    const { ok, error: err } = await apiFetch(`/api/newsletter/subscribe`, {
      method: "POST",
      body: JSON.stringify({
        email,
        lang: lang || "fr",
        source,
        consent: true,
        consent_text: consentText,
        website, // honeypot
      }),
    });
    if (ok) {
      setStatus("ok");
      setEmail("");
      setConsent(false);
    } else {
      setErrorMsg(err || t("newsletter.error.generic"));
      setStatus("err");
    }
  };

  const inputClass = variant === "footer"
    ? "flex-1 bg-white/10 border border-white/20 text-white placeholder:text-white/50 rounded-[5px] px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--gv-pink)] focus:bg-white/15"
    : "flex-1 bg-white border border-[color:var(--gv-line)] text-[color:var(--gv-ink)] placeholder:text-[color:var(--gv-ink)]/45 rounded-[5px] px-4 py-3 text-sm focus:outline-none focus:border-[color:var(--gv-pink)]";
  const checkboxLabelClass = variant === "footer"
    ? "text-[11.5px] text-white/65 leading-snug"
    : "text-[12.5px] text-[color:var(--gv-ink)]/70 leading-snug";

  if (status === "ok") {
    return (
      <div className="flex items-start gap-2" data-testid="newsletter-success">
        <CheckCircle2 size={18} className="text-[color:var(--gv-pink)] mt-0.5 shrink-0" />
        <div className={variant === "footer" ? "text-white/85 text-sm" : "text-[color:var(--gv-ink)] text-sm"}>
          {t("newsletter.success")}
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="newsletter-form" noValidate>
      {/* Honeypot — off-screen, aria-hidden, tab-index -1 so real users
          never see or focus it, but bots that blindly fill every input
          will populate it and the backend will silently drop the submission. */}
      <input
        type="text"
        name="website"
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        style={{ position: "absolute", left: "-10000px", width: "1px", height: "1px", opacity: 0 }}
      />
      <div className={variant === "footer" ? "flex flex-col gap-2 w-full" : "flex gap-2 w-full"}>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t("newsletter.placeholder.email")}
          className={`${inputClass} min-w-0 w-full`}
          data-testid="newsletter-email"
          autoComplete="email"
        />
        <button
          type="submit"
          disabled={status === "sending"}
          className="btn-primary disabled:opacity-50 shrink-0 justify-center"
          data-testid="newsletter-submit"
        >
          {status === "sending" ? <Loader2 size={14} className="animate-spin" /> : t("newsletter.submit")}
        </button>
      </div>
      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5 accent-[color:var(--gv-pink)]"
          data-testid="newsletter-consent"
        />
        <span className={checkboxLabelClass}>{consentText}</span>
      </label>
      {status === "err" && errorMsg && (
        <div className="flex items-start gap-2 text-[12.5px] text-[#ff6b8b]" data-testid="newsletter-error">
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <span>{errorMsg}</span>
        </div>
      )}
    </form>
  );
};

export default NewsletterSignup;
